import {
  MyNotificationsSchema,
  NotificationSettingsSchema,
  RoleNotificationSettingsSchema,
  SavedTableLayoutListSchema,
  SavedTableLayoutSchema,
  UserNotificationSettingsSchema,
} from '@contracts/schemas/preferences.schema';
import type { NotificationChoices, NotificationRule, Role, TableLayout } from '@/domain';
import type {
  MyNotifications,
  NotificationSettingsOverview,
  PreferencesRepository,
  RoleNotificationSettings,
  SavedTableLayout,
  UserNotificationSettings,
} from '@/application/ports/PreferencesRepository';
import { request } from '../client';

/** The rules as they are sent: the five fields and nothing a screen added to them. */
function wireRules(rules: readonly NotificationRule[]) {
  return {
    rules: rules.map(({ type, receives, inApp, push, mandatory }) => ({
      type,
      receives,
      inApp,
      push,
      mandatory,
    })),
  };
}

export class HttpPreferencesRepository implements PreferencesRepository {
  async tableLayouts(): Promise<SavedTableLayout[]> {
    const answer = await request('/preferences/tables', { schema: SavedTableLayoutListSchema });
    return answer.items;
  }

  saveTableLayout(table: string, layout: TableLayout): Promise<SavedTableLayout> {
    return request(`/preferences/tables/${encodeURIComponent(table)}`, {
      method: 'PUT',
      body: { order: layout.order, hidden: layout.hidden, pageSize: layout.pageSize },
      schema: SavedTableLayoutSchema,
    });
  }

  async resetTableLayout(table: string): Promise<void> {
    await request(`/preferences/tables/${encodeURIComponent(table)}`, { method: 'DELETE' });
  }

  myNotifications(): Promise<MyNotifications> {
    return request('/preferences/notifications', { schema: MyNotificationsSchema });
  }

  saveMyNotifications(choices: NotificationChoices): Promise<MyNotifications> {
    return request('/preferences/notifications', {
      method: 'PUT',
      body: { off: [...choices.off], home: choices.home === null ? null : [...choices.home] },
      schema: MyNotificationsSchema,
    });
  }

  notificationSettings(): Promise<NotificationSettingsOverview> {
    return request('/notification-settings', { schema: NotificationSettingsSchema });
  }

  saveRoleNotifications(role: Role, rules: readonly NotificationRule[]): Promise<RoleNotificationSettings> {
    return request(`/notification-settings/roles/${role}`, {
      method: 'PUT',
      body: wireRules(rules),
      schema: RoleNotificationSettingsSchema,
    });
  }

  resetRoleNotifications(role: Role): Promise<RoleNotificationSettings> {
    return request(`/notification-settings/roles/${role}`, {
      method: 'DELETE',
      schema: RoleNotificationSettingsSchema,
    });
  }

  userNotifications(userId: string): Promise<UserNotificationSettings> {
    return request(`/users/${userId}/notification-settings`, {
      schema: UserNotificationSettingsSchema,
    });
  }

  saveUserNotifications(userId: string, rules: readonly NotificationRule[]): Promise<UserNotificationSettings> {
    return request(`/users/${userId}/notification-settings`, {
      method: 'PUT',
      body: wireRules(rules),
      schema: UserNotificationSettingsSchema,
    });
  }

  resetUserNotifications(userId: string): Promise<UserNotificationSettings> {
    return request(`/users/${userId}/notification-settings`, {
      method: 'DELETE',
      schema: UserNotificationSettingsSchema,
    });
  }
}
